// Parcours : le squelette d'étape n'apparaissait qu'une fois la réponse du serveur revenue,
// au moment où la frame `step_form_*` passe en chargement. Entre le clic sur « Suivant » et
// cet instant, l'écran ne bougeait pas — la même latence que celle qu'on a supprimée sur la
// sidebar. On peint donc le squelette dès la soumission.
//
// En OVERLAY et non en remplacement du contenu de la frame : le formulaire qu'on est en train
// de soumettre y est encore, et le retirer du DOM pendant que Turbo pilote sa soumission
// reviendrait à lui couper l'herbe sous le pied.
//
// La source du markup est un <template> rendu par le layout d'étape — même principe que le
// squelette instantané de la nav : le serveur reste seul responsable de la forme.

const OVERLAY_CLASS = 'workflow-step-skeleton-overlay';
const FRAME_SELECTOR = 'turbo-frame[id^="step_form_"]';
const TEMPLATE_SELECTOR = 'template[data-step-skeleton]';
// Moule du squelette qu'on vient de peindre, renvoyé au serveur avec la requête (cf. plus bas).
const SKELETON_HEADER = 'X-Step-Skeleton';
const NAV_ROW_SELECTOR = '[data-workflow-step-nav-row]';
const NAV_ROW_CURRENT_SELECTOR = '[data-workflow-step-nav-row-current="true"]';
// Retour en arrière sur une étape déjà franchie : le lien de la nav (et ceux du recap) visent la
// frame step_layout_, donc c'est tout le parcours qui se recharge, nav comprise.
const NAV_LINK_SELECTOR = 'a[data-turbo-frame^="step_layout_"]';
const NAV_ROW_PENDING_CLASS = 'workflow-step-nav-row--pending';
// L'étape qu'on quitte : elle se retire pendant que le squelette se pose, au lieu d'être
// recouverte sans bouger (cf. `_workflow_step_shell.scss`).
const LEAVING_CLASS = 'workflow-step-frame--leaving';
// Sens du déplacement, posé sur <html> : il doit survivre au remplacement de la frame, qui est
// justement ce qui se passe entre le départ de l'étape et l'arrivée de la suivante. C'est la
// seule information que le CSS ne peut pas déduire tout seul — et c'est elle qui fait la
// différence entre une animation décorative et une qui dit « vous avancez » ou « vous revenez ».
const MOTION_DIRECTION_ATTR = 'stepMotion';
const SAFETY_TIMEOUT_MS = 10_000;
// Sonde de suggestions : le controller `search` resoumet le formulaire de l'étape à chaque frappe
// pour rafraîchir la liste de propositions (searchWithSource), en glissant `source=stimulus` dans
// le corps. C'est le marqueur que ResourcesController lit pour ne rien enregistrer et ne pas
// avancer — rien ne bouge donc dans l'étape, et il n'y a rien à annoncer.
const PROBE_FIELD = 'source';
const PROBE_VALUE = 'stimulus';

let safetyTimeout: number | null = null;

// Corps de la soumission : URLSearchParams en urlencoded (le cas courant), FormData en multipart,
// et les paramètres d'URL sur un GET — les trois répondent à `get`.
//
// On le lit plutôt que d'aller chercher le champ caché dans le formulaire : il est construit à la
// soumission, donc avant que le controller `search` ne retire ce champ, alors que le DOM, lui, a
// déjà été nettoyé quand `turbo:submit-start` arrive.
const isSuggestionProbe = (body: unknown): boolean => {
  const params = body as URLSearchParams | null;
  return typeof params?.get === 'function' && params.get(PROBE_FIELD) === PROBE_VALUE;
};

const removeOverlay = () => {
  if (safetyTimeout !== null) {
    window.clearTimeout(safetyTimeout);
    safetyTimeout = null;
  }
  document.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((node) => node.remove());
  document.querySelectorAll(`${FRAME_SELECTOR}[aria-busy]`).forEach((frame) => frame.removeAttribute('aria-busy'));
  // Indispensable, pas cosmétique : l'animation de sortie garde son dernier état (`both`), donc
  // une étape rendue à nouveau après un refus de validation resterait invisible.
  document.querySelectorAll(`.${LEAVING_CLASS}`).forEach((frame) => frame.classList.remove(LEAVING_CLASS));
  document.querySelectorAll<HTMLElement>(`.${NAV_ROW_PENDING_CLASS}`).forEach((row) => {
    row.classList.remove(NAV_ROW_PENDING_CLASS);
    row.removeAttribute('aria-busy');
    const ticker = row.querySelector<HTMLElement>('.workflow-step-nav-row__ticker');
    ticker?.style.removeProperty('--nav-pending-spinner-size');
    ticker?.style.removeProperty('--nav-pending-spinner-margin');
  });
};

// La ligne visée passe en attente : sa bulle devient un spinner. Sur un « Suivant » c'est l'étape
// en cours — seul signal entre la soumission et le turbo stream qui remplacera la nav pour marquer
// l'étape validée, ou la laisser en l'état si elle ne l'est pas ; sur un clic dans la nav, c'est
// l'étape vers laquelle on part. Le libellé et les badges sont laissés intacts : leur contenu ne
// change pas, les masquer ne ferait que clignoter. Tout l'habillage est en CSS : on ne fait que
// poser une classe, donc rien à reconstruire et rien à remettre en place si la nav est re-rendue
// entre-temps.
const markNavRowPending = (rows: HTMLElement[]) => {
  rows.forEach((row) => {
    // Boîte de la bulle relevée AVANT qu'elle ne soit masquée : le spinner en hérite, donc il
    // se cale au même endroit quel que soit le contexte (nav desktop, copie offcanvas).
    // Rien d'autre n'est figé : seule la bulle est remplacée, et par une boîte de mêmes
    // dimensions — la ligne ne peut donc pas bouger.
    const ticker = row.querySelector<HTMLElement>('.workflow-step-nav-row__ticker');
    const icon = row.querySelector<HTMLElement>('.workflow-step-nav-row__ticker-icon');
    if (ticker && icon) {
      const box = icon.getBoundingClientRect();
      ticker.style.setProperty('--nav-pending-spinner-size', `${box.height}px`);
      ticker.style.setProperty('--nav-pending-spinner-margin', `${Math.max((box.width - box.height) / 2, 0)}px`);
    }

    row.classList.add(NAV_ROW_PENDING_CLASS);
    row.setAttribute('aria-busy', 'true');
  });
};

const currentNavRows = () =>
  Array.from(document.querySelectorAll<HTMLElement>(NAV_ROW_CURRENT_SELECTOR));

// Sens d'un clic dans la nav : la ligne visée est-elle avant ou après l'étape courante ? On
// compare leur ordre dans le document, ce qui est exactement l'ordre des étapes à l'écran.
const navLinkDirection = (link: HTMLAnchorElement): 'forward' | 'back' => {
  const targetRow = link.closest<HTMLElement>(NAV_ROW_SELECTOR);
  const currentRow = document.querySelector<HTMLElement>(NAV_ROW_CURRENT_SELECTOR);
  if (!targetRow || !currentRow || targetRow === currentRow) return 'back';

  const position = currentRow.compareDocumentPosition(targetRow);
  return position & Node.DOCUMENT_POSITION_FOLLOWING ? 'forward' : 'back';
};

// Lignes correspondant à l'étape visée par un lien de nav. La nav est rendue deux fois (panneau
// desktop + copie offcanvas) et chaque ligne porte un second lien dans sa tooltip repliée :
// aucune de ces copies n'a d'identifiant, l'URL de l'étape est le seul fil qui les relie.
const navRowsForLink = (link: HTMLAnchorElement): HTMLElement[] => {
  const rows = new Set<HTMLElement>();

  document.querySelectorAll<HTMLAnchorElement>(NAV_LINK_SELECTOR).forEach((candidate) => {
    if (candidate.href !== link.href) return;

    const row = candidate.closest<HTMLElement>(NAV_ROW_SELECTOR);
    if (row) rows.add(row);
  });

  return Array.from(rows);
};

// Le squelette est peint en haut de la frame (`inset: 0` sur celle-ci). Si l'étape était
// scrollée — formulaire long, clic sur « Suivant » depuis le bas de l'écran — il naissait
// au-dessus de la zone visible : on ne voyait que son fond, et l'étape paraissait vide. On
// remonte donc en haut en même temps qu'on peint, ce qui est de toute façon la position où la
// prochaine étape s'affichera.
//
// Le conteneur de scroll n'est pas le même partout : c'est <main class="step-layout"> en web
// (hauteur figée, `overflow-y: auto`, cf. .full-height-minus-header), le document en iframe
// embarquée, la .step-layout de la frame step_layout_ en natif. Plutôt que de choisir, on
// remonte tous les ancêtres scrollables de la frame.
const scrollStepToTop = (frame: HTMLElement) => {
  for (let node: HTMLElement | null = frame; node; node = node.parentElement) {
    if (node.scrollTop === 0) continue;

    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') node.scrollTop = 0;
  }

  // Le scroll du WKWebView ne se pilote pas d'ici (cf. resetTurboNativeScroll dans
  // height_controller.js) — et en natif le parcours scrolle dans .step-layout, déjà traitée
  // par la boucle ci-dessus.
  if (window.scrollY > 0 && !document.body.classList.contains('turbo-native')) {
    window.scrollTo(0, 0);
  }
};

const skeletonTemplate = () => document.querySelector<HTMLTemplateElement>(TEMPLATE_SELECTOR);

const paintOverlay = (frame: HTMLElement) => {
  if (frame.querySelector(`.${OVERLAY_CLASS}`)) return;

  const template = skeletonTemplate();
  if (!template) return;

  const overlay = document.createElement('div');
  overlay.className = OVERLAY_CLASS;
  overlay.setAttribute('aria-hidden', 'true');
  overlay.appendChild(template.content.cloneNode(true));

  frame.setAttribute('aria-busy', 'true');
  frame.appendChild(overlay);
  // Après l'ajout de l'overlay : la classe ne vise que les enfants qui ne sont pas lui.
  frame.classList.add(LEAVING_CLASS);
  scrollStepToTop(frame);

  safetyTimeout = window.setTimeout(removeOverlay, SAFETY_TIMEOUT_MS);
};

export function initializeWorkflowStepSkeleton() {
  if ((window as any).__workflowStepSkeletonInitialized) return;
  (window as any).__workflowStepSkeletonInitialized = true;

  // Le squelette est peint deux fois pour un seul « Suivant » : l'overlay ici, puis le placeholder
  // que le turbo stream d'avancement pose dans la frame en attendant le contenu réel. Deux moules
  // différents, et la forme change au milieu de l'attente — ce qui se voit d'autant plus que
  // l'attente est longue.
  //
  // Le serveur ne peut pas retomber sur le bon moule tout seul : quand on peint, l'étape d'arrivée
  // n'est pas encore connue, et le nom d'étape qu'il verra au retour n'est pas forcément celui qui
  // a servi à rendre le <template> (étape rejouée en erreur, sous-ressource, résolution du pas
  // suivant). On ne lui laisse donc pas le choix : le moule voyage avec la requête.
  //
  // Sur `turbo:before-fetch-request` et non `turbo:submit-start` : Turbo dispatche le premier
  // AVANT le second, c'est le seul point d'accroche où les en-têtes sont encore ouverts.
  document.addEventListener('turbo:before-fetch-request', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest(FRAME_SELECTOR)) return;

    const { fetchOptions } = (event as CustomEvent).detail;
    if (isSuggestionProbe(fetchOptions.body)) return;

    const mould = skeletonTemplate()?.dataset.stepSkeleton;
    if (mould) fetchOptions.headers[SKELETON_HEADER] = mould;
  });

  document.addEventListener('turbo:submit-start', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const frame = form.closest<HTMLElement>(FRAME_SELECTOR);
    if (!frame) return;

    // Sonde de suggestions : la réponse ne remplace que la frame des propositions. Peindre
    // l'overlay ferait disparaître le formulaire en pleine saisie — et pour longtemps, puisque
    // aucun `turbo:frame-render` sur step_form_ ne viendrait le retirer.
    if (isSuggestionProbe((event as CustomEvent).detail?.formSubmission?.body)) return;

    document.documentElement.dataset[MOTION_DIRECTION_ATTR] = 'forward';
    paintOverlay(frame);
    markNavRowPending(currentNavRows());
  });

  // Retour en arrière (ou saut) vers une autre étape : le lien vise la frame step_layout_, donc
  // c'est tout le parcours qui part en rechargement. Le seul retour visuel était jusqu'ici le flou
  // de `turbo-frame[busy] .turbo-blur`, qui rendait nav et contenu illisibles d'un bloc. On
  // réutilise donc les deux signaux du « Suivant » — spinner sur l'étape visée, squelette dans le
  // contenu — et le flou est neutralisé en CSS tant que l'overlay est là.
  //
  // Sur le clic et non sur `turbo:before-fetch-request` : c'est l'instant du geste, avant même que
  // Turbo n'ait décidé de partir en requête, donc zéro latence perçue.
  document.addEventListener('click', (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest<HTMLAnchorElement>(NAV_LINK_SELECTOR);
    // Étape verrouillée : Bootstrap coupe les pointer-events du lien `.disabled`, le clic arrive
    // donc sur le <span> de tooltip qui l'enveloppe et `closest` ne remonte sur rien. La garde
    // explicite couvre les contextes où cette règle ne s'appliquerait pas : rien ne se chargera,
    // donc rien à annoncer.
    if (!link || link.classList.contains('disabled') || link.getAttribute('aria-disabled') === 'true') return;

    // Un lien de nav mène aussi bien à une étape déjà franchie qu'à une suivante déjà
    // débloquée : c'est la position de la ligne visée dans la nav, et non le fait de cliquer,
    // qui dit le sens. À défaut de repère, on retombe sur « retour » — c'est l'usage de loin le
    // plus fréquent de ces liens.
    document.documentElement.dataset[MOTION_DIRECTION_ATTR] = navLinkDirection(link);

    const frame = document.querySelector<HTMLElement>(FRAME_SELECTOR);
    if (frame) paintOverlay(frame);
    markNavRowPending(navRowsForLink(link));
  });

  // Soumission refusée (validation côté client, erreur réseau) : rien ne va être re-rendu,
  // il faut rendre la main au formulaire.
  document.addEventListener('turbo:submit-end', (event) => {
    if ((event as CustomEvent).detail?.success === false) removeOverlay();
  });

  // Le rendu de la frame remplace ses enfants, donc l'overlay part avec. On nettoie quand
  // même pour les cas où la frame survit, et à chaque navigation.
  document.addEventListener('turbo:frame-render', removeOverlay);
  document.addEventListener('turbo:load', removeOverlay);
  document.addEventListener('turbo:fetch-request-error', removeOverlay);
}
